/**
 * MultiSelectFilter - Chip-based multi-select dropdown for filtering
 * scheduled events by customer, technician, city, state, etc.
 *
 * Composed of:
 *   - A summary button showing "<Label>: N selected" (or just "<Label>"
 *     when nothing is picked).
 *   - A pop-up panel containing a search input and a scrollable list
 *     of checkbox options. Search is plain substring match against the
 *     label, case-insensitive.
 *   - Below the button, the currently-selected values render as
 *     dismissible chips so the user can see + remove individual picks
 *     without re-opening the panel.
 *
 * Fully controlled: parent owns `selectedValues` and receives the new
 * Set on every toggle/clear action.
 *
 * Used by EventFiltersBar to power the customer / tech / city / state
 * filters on the Calendar and Map views.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** A single selectable option in the dropdown. */
export interface MultiSelectOption {
    /** Stable identifier — what gets stored in the selection set */
    value: string;
    /** User-facing label rendered in the option list and in chips */
    label: string;
}

/**
 * Props for MultiSelectFilter.
 *
 * @property label           Field name shown on the summary button
 * @property options         Full option list (component handles search + display)
 * @property selectedValues  Currently-selected values as a Set of stringified ids
 * @property onChange        Called with the next selection on every toggle/clear
 * @property placeholder     Search input placeholder; defaults to "Search..."
 * @property emptyText       Shown when no options match the search query
 * @property className       Optional extra classes for the outer wrapper
 */
export interface MultiSelectFilterProps {
    label: string;
    options: MultiSelectOption[];
    selectedValues: Set<string>;
    onChange: (next: Set<string>) => void;
    placeholder?: string;
    emptyText?: string;
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
    label,
    options,
    selectedValues,
    onChange,
    placeholder = 'Search...',
    emptyText = 'No matches',
    className,
}) => {
    /* Local UI state — only the panel-open flag and the search query
     * are local; selection state is owned by the parent so multiple
     * filters can be combined without per-component re-render thrash. */
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    /* Container ref used to detect outside clicks and auto-close. */
    const containerRef = useRef<HTMLDivElement | null>(null);

    /* Outside-click handler — close the panel when the user clicks
     * anywhere outside the component (including other filters). */
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e: MouseEvent) => {
            const node = containerRef.current;
            if (node && e.target instanceof Node && !node.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    /* Filter options against the current search query. Case-insensitive
     * substring match against the label. Memoized to avoid filtering
     * the full list on unrelated re-renders. */
    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => opt.label.toLowerCase().includes(q));
    }, [options, search]);

    /* Toggle a single option's membership in the selection set. */
    const toggle = (value: string) => {
        const next = new Set(selectedValues);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        onChange(next);
    };

    /* Clear all selections at once. */
    const clearAll = () => {
        onChange(new Set());
    };

    /* Lookup table to render chips with the right label even when the
     * underlying options array is async-loaded. */
    const optionByValue = useMemo(() => {
        const map = new Map<string, MultiSelectOption>();
        for (const o of options) map.set(o.value, o);
        return map;
    }, [options]);

    const selectedCount = selectedValues.size;

    return (
        <div ref={containerRef} className={['multi-select-filter', 'relative', className ?? ''].join(' ')} data-ref="molecule-multi-select-filter">
            {/* Summary button — toggles the panel; shows the count of
             * picks when any are selected. */}
            <button
                type="button"
                onClick={() => setIsOpen((v) => !v)}
                className={[
                    'multi-select-filter__toggle',
                    /* min-h-44px on touch — the toggle is 39px from padding alone,
                       and it sits in a dense filter row where the neighbouring
                       controls are equally easy to hit by mistake. */
                    'inline-flex items-center gap-6px px-12px py-8px min-h-44px desktop:min-h-0 border rounded-md text-14px font-sans bg-white transition-colors',
                    selectedCount > 0
                        ? 'multi-select-filter__toggle--active border-brand-primary text-brand-primary'
                        : 'border-brand-border text-brand-text hover:border-brand-primary/40',
                ].join(' ')}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="font-medium">{label}</span>
                {selectedCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-6px bg-brand-primary text-white text-11px font-medium rounded-full">
                        {selectedCount}
                    </span>
                )}
                {/* Caret */}
                <svg className="w-12px h-12px text-brand-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
            </button>

            {/* Pop-up panel — search + checkbox list */}
            {isOpen && (
                <div
                    className="multi-select-filter__panel absolute z-30 mt-4px w-[260px] max-w-[calc(100vw-32px)] bg-white border border-brand-border rounded-md shadow-lg"
                    role="listbox"
                >
                    <div className="p-8px border-b border-brand-border">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={placeholder}
                            className="w-full px-8px py-6px border border-brand-border rounded-md text-16px focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            /* Auto-focus the search input on open so the user
                             * can start typing immediately without an extra click. */
                            autoFocus
                        />
                    </div>

                    <div className="max-h-[240px] overflow-y-auto py-4px">
                        {filteredOptions.length === 0 && (
                            <div className="px-12px py-8px text-12px text-brand-text-muted">{emptyText}</div>
                        )}
                        {filteredOptions.map((opt) => (
                            <Checkbox
                                key={opt.value}
                                checked={selectedValues.has(opt.value)}
                                onChange={() => toggle(opt.value)}
                                label={opt.label}
                                className="multi-select-filter__option w-full items-center px-12px hover:bg-brand-surface"
                                labelClassName="text-13px"
                                dataRef="molecule-multi-select-filter-option"
                            />
                        ))}
                    </div>

                    {selectedCount > 0 && (
                        <div className="p-8px border-t border-brand-border">
                            <button
                                type="button"
                                onClick={clearAll}
                                className="min-h-44px inline-flex items-center text-12px font-medium text-brand-primary hover:underline"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Selected chips — dismissible inline display of the
             * current picks. Lets the user remove individual values
             * without reopening the panel. */}
            {selectedCount > 0 && (
                <div className="multi-select-filter__chips flex flex-wrap gap-4px mt-6px">
                    {Array.from(selectedValues).map((v) => {
                        const opt = optionByValue.get(v);
                        if (!opt) return null;
                        return (
                            <button
                                key={v}
                                type="button"
                                onClick={() => toggle(v)}
                                className="inline-flex items-center gap-4px px-10px py-6px min-h-44px desktop:min-h-32px text-12px font-sans text-brand-primary bg-brand-primary/10 border border-brand-primary/20 rounded-full hover:bg-brand-primary/20"
                                aria-label={`Remove ${opt.label}`}
                            >
                                {opt.label}
                                <span aria-hidden="true" className="inline-flex items-center justify-center w-16px h-16px">×</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MultiSelectFilter;
